// SPDX-FileCopyrightText: Copyright Orangebot, Inc. and Medplum contributors
// SPDX-License-Identifier: Apache-2.0
import type { MedplumClient } from '@medplum/core';
import type {
  Appointment,
  Bundle,
  HealthcareService,
  Practitioner,
  Provenance,
  Resource,
  Schedule,
  Slot,
} from '@medplum/fhirtypes';

const ENV_TAG_SYSTEM = 'https://polarissleep.com/env';
const TARGET_ENVIRONMENT = import.meta.env.VITE_POLARIS_ENV?.trim() || 'staging';
const MIGRATION_EXTENSION_URL = 'https://polarissleep.com/fhir/StructureDefinition/legacy-appointment-migration';
const SERVICE_REFERENCE_EXTENSION_URL = 'https://medplum.com/fhir/service-type-reference';

export function getMigrationTargetEnvironment(): string {
  return TARGET_ENVIRONMENT;
}

export interface AppointmentMigrationResult {
  appointmentId: string;
  fromEnvironment: string;
  toEnvironment: string;
  migratedResourceCount: number;
}

function getEnvironment(resource: Resource): string | undefined {
  return resource.meta?.tag?.find((tag) => tag.system === ENV_TAG_SYSTEM)?.code;
}

function hasMigrationMarker(appointment: Appointment): boolean {
  return appointment.extension?.some((extension) => extension.url === MIGRATION_EXTENSION_URL) === true;
}

export function needsEnvironmentMigration(appointment: Appointment, targetEnvironment = TARGET_ENVIRONMENT): boolean {
  const environment = getEnvironment(appointment);
  return Boolean(environment && environment !== targetEnvironment && !hasMigrationMarker(appointment));
}

function withEnvironment(resource: Resource, fromEnvironment: string): Resource {
  const existingTags = resource.meta?.tag ?? [];
  const otherTags = existingTags.filter((tag) => tag.system !== ENV_TAG_SYSTEM);
  const currentEnvironment = getEnvironment(resource);
  if (currentEnvironment && currentEnvironment !== fromEnvironment && currentEnvironment !== TARGET_ENVIRONMENT) {
    throw new Error(
      `${resource.resourceType}/${resource.id ?? 'unknown'} belongs to environment ${currentEnvironment}`
    );
  }
  return {
    ...resource,
    meta: {
      ...resource.meta,
      tag: [...otherTags, { system: ENV_TAG_SYSTEM, code: TARGET_ENVIRONMENT }],
    },
  };
}

function getReference(resource: Resource): string {
  if (!resource.id) {
    throw new Error(`${resource.resourceType} is missing an ID`);
  }
  return `${resource.resourceType}/${resource.id}`;
}

function getPractitionerReference(appointment: Appointment, schedule: Schedule): string {
  const appointmentProvider = appointment.participant?.find((participant) =>
    participant.actor?.reference?.startsWith('Practitioner/')
  )?.actor?.reference;
  const scheduleProvider = schedule.actor?.find((actor) => actor.reference?.startsWith('Practitioner/'))?.reference;
  const reference = appointmentProvider ?? scheduleProvider;
  if (!reference) {
    throw new Error('Migration requires an Appointment or Schedule Practitioner reference');
  }
  return reference;
}

function getHealthcareServiceReferences(schedule: Schedule): string[] {
  return [
    ...new Set(
      (schedule.serviceType ?? []).flatMap((serviceType) =>
        (serviceType.extension ?? [])
          .filter((extension) => extension.url === SERVICE_REFERENCE_EXTENSION_URL)
          .map((extension) => extension.valueReference?.reference)
          .filter((reference): reference is string => reference?.startsWith('HealthcareService/') === true)
      )
    ),
  ];
}

async function readRelatedResources(
  medplum: MedplumClient,
  appointment: Appointment,
  fromEnvironment: string
): Promise<Resource[]> {
  if (!appointment.id) {
    throw new Error('Appointment must have an ID before migration');
  }

  const slots = (await Promise.all(
    (appointment.slot ?? [])
      .map((reference) => reference.reference)
      .filter((reference): reference is string => reference?.startsWith('Slot/') === true)
      .map((reference) => medplum.readReference({ reference }))
  )) as Slot[];
  const slotScheduleReferences = [
    ...new Set(
      slots
        .map((slot) => slot.schedule?.reference)
        .filter((reference): reference is string => reference?.startsWith('Schedule/') === true)
    ),
  ];

  const providerReference = appointment.participant?.find((participant) =>
    participant.actor?.reference?.startsWith('Practitioner/')
  )?.actor?.reference;
  let schedules: Schedule[];
  if (slotScheduleReferences.length > 0) {
    schedules = (await Promise.all(
      slotScheduleReferences.map((reference) => medplum.readReference({ reference }))
    )) as Schedule[];
  } else if (providerReference) {
    schedules = await medplum.searchResources('Schedule', {
      actor: providerReference,
      _tag: `${ENV_TAG_SYSTEM}|${fromEnvironment}`,
      _count: '2',
    });
  } else {
    schedules = [];
  }
  if (schedules.length !== 1) {
    throw new Error(`Migration requires exactly one source Schedule; found ${schedules.length}`);
  }
  const schedule = schedules[0];
  if (!schedule) {
    throw new Error('Source Schedule was not found');
  }

  const healthcareServiceReferences = getHealthcareServiceReferences(schedule);
  if (healthcareServiceReferences.length === 0) {
    throw new Error('Source Schedule has no HealthcareService references; link it to v2 services before migrating.');
  }
  const [encounters, practitioner, roles, healthcareServices] = await Promise.all([
    medplum.searchResources('Encounter', { appointment: `Appointment/${appointment.id}`, _count: '100' }),
    medplum.readReference({ reference: getPractitionerReference(appointment, schedule) }) as Promise<Practitioner>,
    medplum.searchResources('PractitionerRole', {
      practitioner: getPractitionerReference(appointment, schedule),
      _tag: `${ENV_TAG_SYSTEM}|${fromEnvironment}`,
      _count: '100',
    }),
    Promise.all(healthcareServiceReferences.map((reference) => medplum.readReference({ reference }))) as Promise<
      HealthcareService[]
    >,
  ]);

  const related: Resource[] = [
    appointment,
    ...slots,
    schedule,
    practitioner,
    ...roles,
    ...healthcareServices,
    ...encounters,
  ];
  for (const resource of related) {
    const environment = getEnvironment(resource);
    if (environment !== fromEnvironment && resource.resourceType !== 'Encounter') {
      throw new Error(`${getReference(resource)} is not tagged ${fromEnvironment}`);
    }
  }
  return related;
}

export async function migrateAppointmentToCurrentEnvironment(
  medplum: MedplumClient,
  appointment: Appointment
): Promise<AppointmentMigrationResult> {
  if (!appointment.id) {
    throw new Error('Appointment must have an ID before migration');
  }
  const fromEnvironment = getEnvironment(appointment);
  if (!fromEnvironment || fromEnvironment === TARGET_ENVIRONMENT) {
    throw new Error(`Appointment is already in ${TARGET_ENVIRONMENT} or has no environment tag`);
  }
  if (hasMigrationMarker(appointment)) {
    throw new Error('Appointment has already been migrated');
  }

  const related = await readRelatedResources(medplum, appointment, fromEnvironment);
  const migratedAt = new Date().toISOString();
  const resources = related.map((resource) => {
    const migrated = withEnvironment(resource, fromEnvironment);
    if (migrated.resourceType === 'Appointment') {
      return {
        ...migrated,
        extension: [
          ...((migrated as Appointment).extension ?? []),
          {
            url: MIGRATION_EXTENSION_URL,
            extension: [
              { url: 'fromEnvironment', valueCode: fromEnvironment },
              { url: 'toEnvironment', valueCode: TARGET_ENVIRONMENT },
              { url: 'migratedAt', valueDateTime: migratedAt },
            ],
          },
        ],
      } satisfies Appointment;
    }
    return migrated;
  });

  const profile = medplum.getProfile();
  const provenance: Provenance = {
    resourceType: 'Provenance',
    target: resources.map((resource) => ({ reference: getReference(resource) })),
    recorded: migratedAt,
    activity: {
      coding: [
        { system: 'https://polarissleep.com/fhir/CodeSystem/migration-activity', code: 'environment-migration' },
      ],
      text: `Migrated legacy appointment resources to ${TARGET_ENVIRONMENT}`,
    },
    reason: [{ text: `Moved environment ${fromEnvironment} to ${TARGET_ENVIRONMENT}` }],
    agent: profile?.id ? [{ who: { reference: getReference(profile) } }] : [],
  };

  const transaction: Bundle = {
    resourceType: 'Bundle',
    type: 'transaction',
    entry: [
      ...resources.map((resource) => ({ request: { method: 'PUT' as const, url: getReference(resource) }, resource })),
      { request: { method: 'POST' as const, url: 'Provenance' }, resource: provenance },
    ],
  };
  await medplum.executeBatch(transaction);

  return {
    appointmentId: appointment.id,
    fromEnvironment,
    toEnvironment: TARGET_ENVIRONMENT,
    migratedResourceCount: resources.length,
  };
}
